import commonAlert from '../../components/v2/tipsAlert'
import Vue from 'vue'
const SwapStage = {
  SwapStageGetTokenList: 1, // the stage of get the token list
  SwapStageGetDepositInfo: 2, // the stage of get deposit info
  SwapStageSwap: 3, // the state of swap
  SwapStageStatus: 4 // the stage of viewing conversion status
}

const SwapType = {
  SwapTypeUnknown: -1, // unknown swap type
  SwapTypeErc20ToBep2: 1, // Erc20 to Bep2
  SwapTypeErc20ToMainNet: 2, // Erc20 to COS main net
  SwapTypeBep2ToErc20: 3, // Bep2 to Erc20
  SwapTypeBep2ToMainNet: 4, // Bep2 to mainNet
  SwapTypeMainNetToErc20: 5, // mainNet to Erc20
  SwapTypeMainNetToBep2: 6, // mainNet to Bep2
  SwapTypeBep20ToMainNet: 7, // Bep20 to mainNet
  SwapTypeMainNetToBep20: 8 // mainNet to Bep20

}

const TokenType = {
  TokenTypeUnknown: -1, // unknown token type
  TokenTypeErc20: 1, // erc20 token
  TokenTypeBep2: 2, // bep2 token
  TokenTypeCosMainNet: 3, // cos main net token
  TokenTypeBep20: 4 // bep20 token
}

const SwapResult = {
  SwapResultFail: 0, // swap fail
  SwapResultSuccess: 1, // swap success
  SwapResultNotNeedTransfer: 2 // swap success but No final transfer required
}

const SwapChargeStatus = {
  SwapChargeStatusNotFree: 0, // deduct fee
  SwapChargeStatusFree: 1 // free
}

const netErrorCode = {
  netErrorCodeSystemError: 500,
  netErrorCodeInternalServerError: 600,
  netErrorCodeSwapAddressInvalid: 602,
  netErrorCodeSwapAddressNotExist: 603,
  netErrorCodeInvalidSwapType: 605,
  netErrorCodeInvalidExchangeMemo: 608
}

const languageType = {
  languageTypeTW: 'zh-tw',
  languageTypeCN: 'zh-cn',
  languageTypeEn: 'en',
  languageTypeJa: 'ja',
  languageTypeKo: 'ko',
  languageTypeVietnam: 'vi',
  languageTypePortugal: 'pt-br',
  languageTypeRussian: 'ru'
}

// ── Swap Type Metadata Lookup Table ──────────────────────────────────
// Single source of truth: every swap type's source/target chain, i18n keys, etc.
const SWAP_TYPE_META = {
  [SwapType.SwapTypeErc20ToBep2]: { source: 'erc20', target: 'bep2', descKey: 'message.token_type_erc20_to_bep2', fee: 0 },
  [SwapType.SwapTypeErc20ToMainNet]: { source: 'erc20', target: 'cos', descKey: 'message.token_type_erc20_to_native', fee: 100 },
  [SwapType.SwapTypeBep2ToErc20]: { source: 'bep2', target: 'erc20', descKey: 'message.token_type_bep2_to_erc20', fee: 0 },
  [SwapType.SwapTypeBep2ToMainNet]: { source: 'bep2', target: 'cos', descKey: 'message.token_type_bep2_to_native', fee: 0 },
  [SwapType.SwapTypeMainNetToErc20]: { source: 'cos', target: 'erc20', descKey: 'message.token_type_native_to_erc20', fee: 100 },
  [SwapType.SwapTypeMainNetToBep2]: { source: 'cos', target: 'bep2', descKey: 'message.token_type_native_to_bep2', fee: 0 },
  [SwapType.SwapTypeBep20ToMainNet]: { source: 'bep20', target: 'cos', descKey: 'message.token_type_bep20_to_native', fee: 10 },
  [SwapType.SwapTypeMainNetToBep20]: { source: 'cos', target: 'bep20', descKey: 'message.token_type_native_to_bep20', fee: 10 }
}

// Chain → i18n key for token label and address label
const CHAIN_LABELS = {
  erc20: { token: 'message.erc20_token', address: 'message.erc20_address', display: 'ERC-20', swapDisplay: 'COS-ERC20' },
  bep2: { token: 'message.bep2_token', address: 'message.bep2_address', display: 'BEP2', swapDisplay: 'COS-BEP2' },
  cos: { token: 'message.main_net_token', address: 'message.main_net_address', display: 'COS MainNet', swapDisplay: 'native COS' },
  bep20: { token: 'message.bep20_token', address: 'message.bep20_address', display: 'BEP-20', swapDisplay: 'COS-BEP20' }
}

// Chain → token type (maps chain string back to TokenType enum)
const CHAIN_TO_TOKEN_TYPE = {
  erc20: TokenType.TokenTypeErc20,
  bep2: TokenType.TokenTypeBep2,
  cos: TokenType.TokenTypeCosMainNet,
  bep20: TokenType.TokenTypeBep20
}

// ── Blockchain Explorer URLs ─────────────────────────────────────────
const EXPLORER_URLS = {
  prod: {
    cos: { tx: 'https://explorer.contentos.io/#/tx/', addr: 'https://explorer.contentos.io/#/account/' },
    erc20: { tx: 'https://etherscan.io/tx/', addr: 'https://etherscan.io/address/' },
    bep20: { tx: 'https://bscscan.com/tx/', addr: 'https://bscscan.com/address/' },
    bep2: { tx: 'https://explorer.binance.org/tx/', addr: 'https://explorer.binance.org/address/' }
  },
  dev: {
    cos: { tx: 'https://explorer.contentos.io/#/tx/', addr: 'https://explorer.contentos.io/#/account/' },
    erc20: { tx: 'https://etherscan.io/tx/', addr: 'https://etherscan.io/address/' },
    bep20: { tx: 'https://bscscan.com/tx/', addr: 'https://bscscan.com/address/' },
    bep2: { tx: 'https://explorer.binance.org/tx/', addr: 'https://explorer.binance.org/address/' }
  }
}

function getExplorerUrls () {
  return process.env.NODE_ENV !== 'production' ? EXPLORER_URLS.dev : EXPLORER_URLS.prod
}

// ── Address Placeholder per target chain ─────────────────────────────
const ADDRESS_PLACEHOLDERS = {
  prod: {
    erc20: 'e.g. 0xcE46FCEC0e6D568B78b90d64803038ceC6B4eDed',
    bep20: 'e.g. 0xcE46FCEC0e6D568B78b90d64803038ceC6B4eDed',
    bep2: 'e.g. bnb1mmxvnhkyqrvd2dpskvsgl8lmft4tnrcs97apr3',
    cos: 'e.g. initminer'
  },
  dev: {
    erc20: 'e.g. 0xcE46FCEC0e6D568B78b90d64803038ceC6B4eDed',
    bep20: 'e.g. 0xcE46FCEC0e6D568B78b90d64803038ceC6B4eDed',
    bep2: 'e.g. tbnb1hvsh83e5ttdl6t3ngmysy0k2l5yxfw23862nx3',
    cos: 'e.g. initminer'
  }
}

const swapTypeKey = 'SwapType'
const tokenTypeKey = 'Type'
const memoKey = 'Memo'
const addressKey = 'Address'
const resultStatusKey = 'Status'
const swapAmountKey = 'SwapAmount'
const depositAmountKey = 'DepositAmount'
const depositTxHashKey = 'DepositHash'
const resSenderKey = 'From'
const resReceiverKey = 'To'
const resTxHashKey = 'TxHash'
const isFreeKey = 'IsFree'
const feeDescKey = 'FeeDesc'
const statusKey = 'Status'
const infoKey = 'Info'
const msgKey = 'Msg'
const swapAddressKey = 'swapAddress'
const exChangeMemo = 'exChangeMemo'

export default {
  SwapStage,
  TokenType,
  SwapType,
  SwapResult,
  SWAP_TYPE_META,
  CHAIN_LABELS,
  CHAIN_TO_TOKEN_TYPE,
  swapTypeKey,
  tokenTypeKey,
  memoKey,
  addressKey,
  resultStatusKey,
  depositAmountKey,
  depositTxHashKey,
  swapAmountKey,
  resSenderKey,
  resReceiverKey,
  resTxHashKey,
  isFreeKey,
  feeDescKey,
  SwapChargeStatus,
  statusKey,
  infoKey,
  msgKey,
  swapAddressKey,
  exChangeMemo,
  netErrorCode,
  languageType,

  // ── Meta helpers ───────────────────────────────────────────────────
  getSwapMeta (swapType) {
    return SWAP_TYPE_META[swapType] || null
  },

  getSourceChain (swapType) {
    const m = SWAP_TYPE_META[swapType]
    return m ? m.source : null
  },

  getTargetChain (swapType) {
    const m = SWAP_TYPE_META[swapType]
    return m ? m.target : null
  },

  getSourceTokenKey (swapType) {
    const m = SWAP_TYPE_META[swapType]
    return m ? CHAIN_LABELS[m.source].token : null
  },

  getTargetAddressKey (swapType) {
    const m = SWAP_TYPE_META[swapType]
    return m ? CHAIN_LABELS[m.target].address : null
  },

  getSwapDescKey (swapType) {
    const m = SWAP_TYPE_META[swapType]
    return m ? m.descKey : null
  },

  getTargetTokenType (swapType) {
    const m = SWAP_TYPE_META[swapType]
    return m ? CHAIN_TO_TOKEN_TYPE[m.target] : TokenType.TokenTypeUnknown
  },

  getChainDisplay (chain) {
    const c = CHAIN_LABELS[chain]
    return c ? c.display : ''
  },

  getChainSwapDisplay (chain) {
    const c = CHAIN_LABELS[chain]
    return c ? c.swapDisplay : ''
  },

  // ── Explorer link helpers ──────────────────────────────────────────
  getTxExplorerLink (chain, hash) {
    const urls = getExplorerUrls()
    const entry = urls[chain]
    return entry ? entry.tx + encodeURIComponent(hash) : ''
  },

  getAddressExplorerLink (chain, addr) {
    const urls = getExplorerUrls()
    const entry = urls[chain]
    return entry ? entry.addr + encodeURIComponent(addr) : ''
  },

  // ── Address placeholder ────────────────────────────────────────────
  getAddressPlaceholder (swapType) {
    const m = SWAP_TYPE_META[swapType]
    if (!m) return ''
    const env = process.env.NODE_ENV !== 'production' ? 'dev' : 'prod'
    return ADDRESS_PLACEHOLDERS[env][m.target] || ''
  },

  // ── Legacy check functions (rewritten with lookup table) ───────────
  checkIsErc20Swap (swapType) {
    const m = SWAP_TYPE_META[swapType]
    return m ? m.source === 'erc20' : false
  },

  checkIsBep2Swap (swapType) {
    const m = SWAP_TYPE_META[swapType]
    return m ? m.source === 'bep2' : false
  },

  checkIsCosMainNetSwap (swapType) {
    const m = SWAP_TYPE_META[swapType]
    return m ? m.source === 'cos' : false
  },

  checkIsBep20Swap (swapType) {
    const m = SWAP_TYPE_META[swapType]
    return m ? m.source === 'bep20' : false
  },

  checkIsSwapToErc20 (swapType) {
    const m = SWAP_TYPE_META[swapType]
    return m ? m.target === 'erc20' : false
  },

  checkIsSwapToBep2 (swapType) {
    const m = SWAP_TYPE_META[swapType]
    return m ? m.target === 'bep2' : false
  },

  checkIsSwapToCosMainNet (swapType) {
    const m = SWAP_TYPE_META[swapType]
    return m ? m.target === 'cos' : false
  },

  checkIsSwapToBep20 (swapType) {
    const m = SWAP_TYPE_META[swapType]
    return m ? m.target === 'bep20' : false
  },

  checkIsNotNullObj (obj) {
    if (obj != null && typeof obj !== 'undefined') {
      return true
    }
    return false
  },

  checkIsDev () {
    return process.env.NODE_ENV !== 'production'
  },

  showAlert (title, desc) {
    let TipsAlert = Vue.extend(commonAlert)
    let alert = new TipsAlert()
    let config = {}
    config.title = title
    config.content = desc
    alert.showCommonAlert(config)
  },

  /*
   * check if bnb address is the exchange address
   */
  checkIsBnbExchangeAddr (addr) {
    if (!this.checkIsDev()) {
      if (addr === 'bnb1p89xmrejmqpwmkye4z2pwtrw49n2vykf3nax33' ||
        addr === 'bnb136ns6lfw4zs5hg4n85vdthaad7hq5m4gtkgf23' ||
        addr === 'bnb16wdyjpl7qzs4hmmt3fpp6fsd63ew385phkqw48' ||
        addr === 'bnb153qsnglpzc3zdfcu6ghnqeyx9vzuaf258lu5l9' ||
        addr === 'bnb165q9dz39mqh789zuuuqwkv22plut6f4nzy9jc9') {
        return true
      }
    } else if (addr === 'tbnb1ewag6r47hdw8lalxln0pme3qks5p7nqh98cw80') {
      return true
    }

    return false
  },

  isValidBnbExchangeMemo (s) {
    return /^\d+$/.test(s)
  },

  checkIsCosMainNetExchangeAddr (addr) {
    if (!this.checkIsDev()) {
      if (addr === 'bitrueprodcos') {
        return true
      }
    } else if (addr === 'bitruetest') {
      return true
    }

    return false
  },

  checkIsSupportLanguage (lCode) {
    for (let i in languageType) {
      if (lCode === languageType[i]) {
        return true
      }
    }
    return false
  },

  serviceFee (swapType) {
    const m = SWAP_TYPE_META[swapType]
    return m ? m.fee : 0
  }

}
